import { IcHeight, IcStatusEllipse, IcStatusLosange } from '@assets/icons/24x24';
import { HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectHeightUnit } from '@redux/settings/settings.selectors';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { getAlertLevelColor, isManualAlert, isSpaceViolationAlert } from '@utils/data/notification.utils';
import { formatDate } from '@utils/date.utils';
import { convertDistanceUnitFromMeter, formatUnit } from '@utils/unit.utils';
import { format } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { AlertType, DroneAlertLevelEnum } from '@/types/data/data.types';
import { DateTimeEnum } from '@/types/dateTime.types';

export default function Alert({ alert }: Readonly<{ alert: AlertType }>) {
  const isDrone = Object.keys(DroneAlertLevelEnum).includes(alert.level);
  const color = getAlertLevelColor(alert.level);
  const situationTime = useAppSelector(selectSituationTime) ?? Date.now();
  const heightUnit = useAppSelector(selectHeightUnit);

  const endTime = alert.endTime ?? situationTime;
  const formattedStarDate = formatDate(alert.startTime, DateTimeEnum.DATE);
  const formattedEndDate = formatDate(endTime, DateTimeEnum.DATE);
  const formattedStartTime = format(new Date(alert.startTime), "HH':'mm':'ss");
  const formattedEndTime = format(new Date(endTime), "HH':'mm':'ss");
  const isSameDay = formattedStarDate === formattedEndDate;
  return (
    <HStack width="100%" gap={1.5} padding={3} alignItems="start" backgroundColor="neutral.700">
      <Icon as={isDrone ? IcStatusLosange : IcStatusEllipse} boxSize="24px" color={color} />
      <VStack gap={2} width="100%" alignItems="start">
        <VStack width="100%" gap={0.5} alignItems="start">
          <Text
            textStyle="md"
            fontWeight="medium"
            paddingY={0.25}
            paddingX={0.5}
            backgroundColor="neutral.black"
            color={color}
          >
            <FormattedMessage id={`alert.level.${isManualAlert(alert) ? 'MANUAL_' : ''}${alert.level}`} />
          </Text>
          <Text color="neutral.300" whiteSpace="pre-line">
            <FormattedMessage
              id={
                isSameDay
                  ? 'components.notification.detail.platforms.sameDay'
                  : 'components.notification.detail.platforms.multipleDays'
              }
              values={{
                startDate: (
                  <Text key="startDate" as="span" color="neutral.white">
                    {formattedStarDate}
                  </Text>
                ),
                startTime: (
                  <Text key="startTime" as="span" color="neutral.white">
                    {formattedStartTime}
                  </Text>
                ),
                endDate: (
                  <Text key="endDate" as="span" color="neutral.white">
                    {formattedEndDate}
                  </Text>
                ),
                endTime: (
                  <Text key="endTime" as="span" color="neutral.white">
                    {formattedEndTime}
                  </Text>
                ),
              }}
            />
          </Text>
        </VStack>
        {isSpaceViolationAlert(alert) && (
          <VStack width="100%" alignItems="start" gap={1}>
            <Text fontWeight="medium">{alert.zoneName}</Text>
            <HStack flex={1} gap={1}>
              <Icon as={IcHeight} boxSize="24px" color="neutral.300" />
              <HStack gap={1}>
                <Text color="neutral.300" textStyle="md">
                  <FormattedMessage id="global.min" />
                </Text>
                <Text color="neutral.white" textStyle="md">
                  {formatUnit(heightUnit, convertDistanceUnitFromMeter(heightUnit, alert.heightMin))}
                </Text>
              </HStack>
              <HStack gap={1} marginLeft={1}>
                <Text color="neutral.300" textStyle="md">
                  <FormattedMessage id="global.max" />
                </Text>
                <Text color="neutral.white" textStyle="md">
                  {formatUnit(heightUnit, convertDistanceUnitFromMeter(heightUnit, alert.heightMax))}
                </Text>
              </HStack>
            </HStack>
          </VStack>
        )}
      </VStack>
    </HStack>
  );
}
